(() => {
  const SECTION_SELECTOR = '[data-organizer-information]';
  const FIELD_SELECTOR = '[data-organizer-field]';
  const REQUIRED_SELECTOR = '[data-required-field]';
  const CHECKOUT_SELECTOR =
    'button[name="checkout"], input[name="checkout"], #checkout';

  const SAVE_DELAY = 500;

  class OrganizerInformation {
    constructor(section) {
      this.section = section;

      this.fields = Array.from(
        section.querySelectorAll(FIELD_SELECTOR)
      );

      this.requiredFields = Array.from(
        section.querySelectorAll(REQUIRED_SELECTOR)
      );

      this.errorMessage = section.querySelector(
        '[data-organizer-error]'
      );

      this.savingMessage = section.querySelector(
        '[data-organizer-saving]'
      );

      this.savedMessage = section.querySelector(
        '[data-organizer-saved]'
      );

      this.saveTimer = null;
      this.savePromise = Promise.resolve();
      this.savedMessageTimer = null;
      this.isCheckingOut = false;

      this.handleFieldInput = this.handleFieldInput.bind(this);
      this.handleFieldBlur = this.handleFieldBlur.bind(this);
      this.handleCheckoutClick = this.handleCheckoutClick.bind(this);
      this.handleCartSubmit = this.handleCartSubmit.bind(this);

      this.initialize();
    }

    initialize() {
      this.fields.forEach((field) => {
        field.addEventListener('input', this.handleFieldInput);
        field.addEventListener('change', this.handleFieldBlur);
        field.addEventListener('blur', this.handleFieldBlur);
      });

      /*
       * Capture every click on a checkout button before Dawn or
       * another script can send the customer to checkout.
       */
      document.addEventListener(
        'click',
        this.handleCheckoutClick,
        true
      );

      /*
       * This is an additional safeguard for checkout forms submitted
       * by pressing Enter or by another script.
       */
      const cartForm = document.getElementById('cart');

      if (cartForm) {
        cartForm.addEventListener(
          'submit',
          this.handleCartSubmit,
          true
        );
      }
    }

    handleFieldInput(event) {
      const field = event.currentTarget;

      if (field.value.trim() !== '') {
        field.setAttribute('aria-invalid', 'false');
      }

      this.hideError();
      this.scheduleSave();
    }

    handleFieldBlur() {
      this.clearScheduledSave();
      this.saveAttributes();
    }

    handleCheckoutClick(event) {
      const checkoutButton = event.target.closest(
        CHECKOUT_SELECTOR
      );

      if (!checkoutButton) return;

      /*
       * Stop Shopify/Dawn from leaving the cart until our information
       * has successfully finished saving.
       */
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.checkout(checkoutButton);
    }

    handleCartSubmit(event) {
      const submitter = event.submitter;

      if (!submitter) return;

      const isCheckout =
        submitter.name === 'checkout' ||
        submitter.id === 'checkout';

      if (!isCheckout) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.checkout(submitter);
    }

    scheduleSave() {
      this.clearScheduledSave();

      this.saveTimer = window.setTimeout(() => {
        this.saveAttributes();
      }, SAVE_DELAY);
    }

    clearScheduledSave() {
      if (this.saveTimer) {
        window.clearTimeout(this.saveTimer);
        this.saveTimer = null;
      }
    }

    getAttributes() {
      const attributes = {};

      this.fields.forEach((field) => {
        const attributeName = field.dataset.attributeName;

        if (!attributeName) return;

        attributes[attributeName] = field.value.trim();
      });

      return attributes;
    }

    validate() {
      let valid = true;
      let firstInvalidField = null;

      this.requiredFields.forEach((field) => {
        const isInvalid = field.value.trim() === '';

        field.setAttribute(
          'aria-invalid',
          isInvalid ? 'true' : 'false'
        );

        if (isInvalid) {
          valid = false;

          if (!firstInvalidField) {
            firstInvalidField = field;
          }
        }
      });

      if (!valid) {
        this.showError();

        if (firstInvalidField) {
          firstInvalidField.focus();

          firstInvalidField.scrollIntoView({
            behavior: 'smooth',
            block: 'center'
          });
        }
      } else {
        this.hideError();
      }

      return valid;
    }

    async saveAttributes() {
      this.clearScheduledSave();
      this.showSaving();

      const attributes = this.getAttributes();

      this.savePromise = fetch(
        `${window.Shopify.routes.root}cart/update.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            attributes
          })
        }
      )
        .then(async (response) => {
          if (!response.ok) {
            throw new Error(
              `Cart update failed with status ${response.status}`
            );
          }

          return response.json();
        })
        .then((cart) => {
          this.showSaved();
          return cart;
        })
        .catch((error) => {
          console.error(
            'Organizer information could not be saved:',
            error
          );

          this.hideSaving();
          this.showSaveError();

          throw error;
        });

      return this.savePromise;
    }

    async checkout(checkoutButton) {
      /*
       * Prevent duplicate saves if the customer clicks more than once.
       */
      if (this.isCheckingOut) return;

      if (!this.validate()) return;

      this.isCheckingOut = true;
      this.clearScheduledSave();

      const isInputButton =
        checkoutButton.tagName === 'INPUT';

      const originalText = isInputButton
        ? checkoutButton.value
        : checkoutButton.textContent;

      checkoutButton.disabled = true;

      if (isInputButton) {
        checkoutButton.value = 'Saving…';
      } else {
        checkoutButton.textContent = 'Saving…';
      }

      try {
        /*
         * Do not proceed until Shopify confirms that the attributes
         * have been saved to the cart.
         */
        await this.saveAttributes();

        /*
         * Only after a successful response do we send the customer
         * to the normal Shopify checkout.
         */
        window.location.assign(
          `${window.Shopify.routes.root}checkout`
        );
      } catch (error) {
        this.isCheckingOut = false;
        checkoutButton.disabled = false;

        if (isInputButton) {
          checkoutButton.value = originalText;
        } else {
          checkoutButton.textContent = originalText;
        }

        this.showSaveError();
      }
    }

    showError() {
      if (this.errorMessage) {
        this.errorMessage.textContent =
          this.section.dataset.errorMessage ||
          'Please complete the required information before checking out.';

        this.errorMessage.hidden = false;
      }
    }

    hideError() {
      if (this.errorMessage) {
        this.errorMessage.hidden = true;
      }
    }

    showSaving() {
      if (this.savedMessageTimer) {
        window.clearTimeout(this.savedMessageTimer);
      }

      if (this.savedMessage) {
        this.savedMessage.hidden = true;
      }

      if (this.savingMessage) {
        this.savingMessage.hidden = false;
      }
    }

    hideSaving() {
      if (this.savingMessage) {
        this.savingMessage.hidden = true;
      }
    }

    showSaved() {
      this.hideSaving();

      if (!this.savedMessage) return;

      this.savedMessage.hidden = false;

      this.savedMessageTimer = window.setTimeout(() => {
        this.savedMessage.hidden = true;
      }, 1800);
    }

    showSaveError() {
      this.hideSaving();

      if (!this.errorMessage) return;

      this.errorMessage.textContent =
        'The information could not be saved. Please wait a moment and try again.';

      this.errorMessage.hidden = false;
    }
  }

  function initializeOrganizerInformation(root = document) {
    root.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (section.dataset.organizerInitialized === 'true') {
        return;
      }

      section.dataset.organizerInitialized = 'true';

      new OrganizerInformation(section);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initializeOrganizerInformation();
    });
  } else {
    initializeOrganizerInformation();
  }

  /*
   * Reinitialize the section when Shopify reloads it inside
   * the Theme Editor.
   */
  document.addEventListener('shopify:section:load', (event) => {
    initializeOrganizerInformation(event.target);
  });
})();